import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Gift, Check, X } from "lucide-react";

import ReferralFormPopup from "../../states/actions/ReferralFormPopup";

const DISMISS_KEY = "referral_sidebar_dismissed_until";
const DISMISS_DAYS = 30;

// Floating sidebar on /search-property that invites real paying customers
// to submit a referral. Dismissible for 30 days via a per-device localStorage
// flag — that's intentional: each device dismisses independently so the
// program stays visible if a user signs in from a new browser. Server-side
// per-user dismissal would be a separate backend change.
//
// Eligibility (matches the spec's "active Rhino subscriber" = real paying
// customer): subscription_status === 1, NOT a trial, NOT currently paused.
const isPayingCustomer = (member) => {
  const sub = member?.mem_subscription;
  if (!sub) return false;
  if (Number(sub.subscription_status) !== 1) return false;
  if (Number(sub.is_trial) === 1) return false;
  if (Number(member?.mem_pause_status) === 1) return false;
  return true;
};

// Test-user allow-list. Lets designated QA accounts see the sidebar without
// needing ?ref_debug=1 in the URL or modifying their subscription state.
// Populate REACT_APP_REFERRAL_TEST_EMAILS as a comma-separated list per
// environment (e.g. only set on staging; leave empty in prod). Emails are
// normalized to lower-case for comparison.
const TEST_USER_EMAILS = (process.env.REACT_APP_REFERRAL_TEST_EMAILS || "")
  .split(",")
  .map((s) => s.trim().toLowerCase())
  .filter(Boolean);

const isTestUser = (member) => {
  if (TEST_USER_EMAILS.length === 0) return false;
  const email = member?.mem_email?.toLowerCase?.()?.trim();
  return !!email && TEST_USER_EMAILS.includes(email);
};

const isEligible = (member) =>
  isPayingCustomer(member) || isTestUser(member);

// Only show on the bare map view of /search-property — hide as soon as the
// user runs a search or applies any URL-driven filter, so the sidebar
// doesn't crowd the results table / property list view.
// Mirrors SearchMap.jsx's `hasNoValidParams` check so visibility flips at
// the same moment the map itself does.
const isMapView = (search) => {
  try {
    const params = new URLSearchParams(search || "");
    const entries = Array.from(params.entries());
    if (entries.length === 0) return true;
    return entries.every(([, value]) => !value || value === "null");
  } catch (_) {
    return true;
  }
};

const isDismissedNow = () => {
  try {
    const until = localStorage.getItem(DISMISS_KEY);
    if (!until) return false;
    const untilTs = Date.parse(until);
    if (Number.isNaN(untilTs)) return false;
    return Date.now() < untilTs;
  } catch (_) {
    return false;
  }
};

// Debug helpers — activated only when the page URL contains ?ref_debug=1.
// Lets a tester confirm React is mounting the component and inspect what
// the gate sees, even when the gate would otherwise return null. Safe to
// leave on production: regular visitors never pass the flag, so they see
// nothing different.
const isDebugMode = () => {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("ref_debug") === "1";
  } catch (_) {
    return false;
  }
};

const ReferralSidebar = () => {
  const member = useSelector(
    (state) => state?.fetchSiteSettings?.site_settings?.member
  );
  const location = useLocation();

  // Track dismissal in state too so React re-renders when the user clicks X.
  const [dismissed, setDismissed] = useState(isDismissedNow);
  const [showForm, setShowForm] = useState(false);

  // Re-check on mount in case the date crossed past the stored expiry while
  // the SPA was kept open.
  useEffect(() => {
    setDismissed(isDismissedNow());
  }, []);

  const debug = isDebugMode();
  const onMapView = isMapView(location?.search);
  const gatePass = isEligible(member) && onMapView;

  // Diagnostic logging — only when ?ref_debug=1 is in the URL so we don't
  // spam the console for normal visitors.
  if (debug) {
    // eslint-disable-next-line no-console
    console.log("[ReferralSidebar] render", {
      gatePass,
      via: isPayingCustomer(member)
        ? "paying_customer"
        : isTestUser(member)
        ? "test_user_allowlist"
        : "none",
      dismissed,
      test_user_allowlist_size: TEST_USER_EMAILS.length,
      member: member
        ? {
            mem_email: member.mem_email,
            super_admin: member.super_admin,
            mem_pause_status: member.mem_pause_status,
            has_mem_subscription: !!member.mem_subscription,
            subscription_status: member?.mem_subscription?.subscription_status,
            is_trial: member?.mem_subscription?.is_trial,
            plan_name: member?.mem_subscription?.plan_name,
          }
        : null,
    });
  }

  const handleDismiss = () => {
    try {
      const until = new Date();
      until.setDate(until.getDate() + DISMISS_DAYS);
      localStorage.setItem(DISMISS_KEY, until.toISOString());
    } catch (_) {
      // localStorage may be blocked (private browsing). Still hide for this
      // session.
    }
    setDismissed(true);
    // When this aside unmounts, the flex map sibling redistributes to take
    // the whole row, but mapbox-gl's canvas doesn't notice React tree
    // changes — it sticks at the old width until something triggers a
    // resize. Fire a window resize event on the next tick (after React has
    // committed the unmount) so the map recalculates its canvas to fit.
    // Without this, dismissing the sidebar leaves a blank gap until the
    // user refreshes the page.
    setTimeout(() => {
      try {
        window.dispatchEvent(new Event("resize"));
      } catch (_) {
        // No-op — some embedded contexts disallow synthetic events.
      }
    }, 0);
  };

  // Debug short-circuit: when ?ref_debug=1, always render the marker so the
  // tester can confirm React mounted this component, AND bypass the gate so
  // the actual sidebar is visible regardless of eligibility. Click the
  // marker text to copy the gate verdict to clipboard.
  const debugMarker = debug ? (
    <div
      style={{
        position: "fixed",
        top: 8,
        right: 8,
        zIndex: 9999,
        padding: "6px 10px",
        background: gatePass ? "#67773F" : "#b00020",
        color: "#fff",
        fontSize: 12,
        fontWeight: 700,
        borderRadius: 4,
        boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
        fontFamily: "monospace",
      }}
      title="?ref_debug=1 active — drop the param to hide this."
    >
      🔴 RS mounted · gate {gatePass ? "PASS" : "FAIL"}
      {dismissed ? " · dismissed" : ""}
    </div>
  ) : null;

  // Non-debug: respect the gate and dismissal. Debug: always render the card
  // so we can see it on staging regardless of eligibility data.
  if (!debug && (!isEligible(member) || !onMapView || dismissed)) {
    return debugMarker;
  }

  return (
    <>
      {debugMarker}
      <aside
        className="referral_sidebar"
        style={{
          // Rendered inline as a flex sibling of the map so the map shrinks
          // to fit instead of being overlapped. When this component returns
          // null (dismissed, ineligible, or no eligibility data yet), the
          // flex layout collapses to the map alone and Mapbox's
          // ResizeObserver re-fits the canvas to the full width
          // automatically — no manual map.resize() call needed.
          //
          // Margins on this aside are tuned for desktop. Mobile/tablet
          // overrides live in App.css (.search_map_with_sidebar @media
          // (max-width: 991px)) which switch to a stacked layout below the
          // map at full width.
          flex: "0 0 320px",
          alignSelf: "flex-start",
          // 12px below the fixed filter bar, which wraps taller on iPad
          // widths (100px when it is one row). See useHeightCssVar.
          marginTop: "calc(var(--search-filter-bar-height, 88px) + 12px)",
          marginLeft: 20,
          marginRight: 20,
          background: "#fff",
          border: "1px solid #d8dec6",
          borderRadius: 10,
          padding: 24,
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Hide referral banner for 30 days"
          title="Hide banner"
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            width: 32,
            height: 32,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 999,
            border: "1px solid #d8dec6",
            background: "#fff",
            color: "#5a6047",
            cursor: "pointer",
          }}
        >
          <X size={16} strokeWidth={2.25} />
        </button>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 14,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "#f6f8ef",
              border: "1px solid #d8dec6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Gift size={26} color="#67773F" strokeWidth={2} />
          </div>
        </div>

        <h5
          style={{
            margin: 0,
            textAlign: "center",
            fontSize: 22,
            fontWeight: 700,
            color: "#1f2a14",
            lineHeight: 1.2,
          }}
        >
          Earn $200
          <br />
          Per Referral
        </h5>

        <p
          style={{
            margin: "14px 0 16px",
            textAlign: "center",
            fontSize: 13,
            color: "#5a6047",
            lineHeight: 1.5,
          }}
        >
          Know another contractor who could benefit from Rhino Recon?
        </p>

        <p
          style={{
            margin: "0 0 14px",
            textAlign: "center",
            fontSize: 13,
            color: "#1f2a14",
            lineHeight: 1.5,
          }}
        >
          Refer them and earn{" "}
          <strong style={{ color: "#67773F" }}>$200</strong> when they
          become a paying subscriber.
        </p>

        <p
          style={{
            margin: "0 0 14px",
            textAlign: "center",
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          <a
            href="/terms-conditions"
            style={{
              color: "#67773F",
              fontWeight: 700,
              textDecoration: "underline",
            }}
          >
            Terms & Conditions apply
          </a>
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#f6f8ef",
            border: "1px solid #d8dec6",
            padding: "10px 12px",
            borderRadius: 6,
            marginBottom: 14,
            fontSize: 13,
            fontWeight: 600,
            color: "#1f2a14",
          }}
        >
          <Check size={18} color="#67773F" strokeWidth={3} />
          <span>No limit on referrals.</span>
        </div>

        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="site_btn"
          style={{ width: "100%" }}
        >
          Submit a Referral
        </button>

        <p
          style={{
            marginTop: 14,
            marginBottom: 14,
            fontSize: 12,
            textAlign: "center",
            color: "#5a6047",
            lineHeight: 1.4,
          }}
        >
          Questions? Contact us at
          <br />
          <a
            href="mailto:support@rhinorecon.com"
            style={{ color: "#67773F", fontWeight: 600 }}
          >
            support@rhinorecon.com
          </a>
        </p>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Hide referral banner for 30 days"
          style={{
            width: "100%",
            minHeight: 42,
            borderRadius: 6,
            border: "1px solid #d8dec6",
            background: "#fff",
            color: "#5a6047",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Hide this banner
        </button>
      </aside>

      {showForm && <ReferralFormPopup onClose={() => setShowForm(false)} />}
    </>
  );
};

export default ReferralSidebar;
